import { useCallback, useEffect, useState } from 'react';
import LoadingPanel from '../../Abstracts/Components/LoadingPanel';
import EditValueModal from '../../Abstracts/Components/EditValueModal';
import useEftposPosLists from '../../Hooks/UseEftposPosLists';
import useEftposBookings from '../../Hooks/UseEftposBookings';
import useEftposDepositAccounts from '../../Hooks/UseEftposDepositAccounts';
import useUserApi, { ErrorHandler } from '../../Services/UserApi';
import { DepositAccountWarning } from './DepositAccounts/DepositAccountList';
import { MenuWarning } from './Menus/MenuList';
import EftposBookingsTable from './Bookings/EftposBookingsTable';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import Alert from './Alert';
import Modal from '../../Abstracts/Components/Modal';

interface EftposClientData {
	MarketClientABN: string;
	MarketClientGSTRegistered: number | null;
	MarketClientNoABNDeclaration: number;
}

export default function Dashboard() {
	const [modals, setModals] = useState<{ [key: string]: boolean }>({
		abnModal: false,
	});

	const { eftposBookings, setEftposBookings } = useEftposBookings();
	const eftposDepositAccounts = useEftposDepositAccounts();
	const eftposPosLists = useEftposPosLists();

	const [eftposClientData, setEftposClientData] = useState<EftposClientData>();

	const userApi = useUserApi();

	useEffect(() => {
		userApi<{ eftposClientData: EftposClientData }>({
			route: `eftpos/api`,
			data: {
				action: 'getEftposClientData',
				errorMessage: 'Error loading eftpos data',
			},
		})
			.then((res) => {
				setEftposClientData(res.data.eftposClientData);
			})
			.catch((e: unknown) => ErrorHandler.toast(e));
	}, [userApi]);

	const saveEftposClientData = useCallback(
		async (newEftposClientData: EftposClientData) => {
			const res = await userApi<{ eftposClientData: EftposClientData }>({
				route: `eftpos/api`,
				data: {
					action: 'saveEftposClientData',
					eftposClientData: newEftposClientData,
				},
				errorMessage: 'Error saving eftpos data',
			});
			setEftposClientData(res.data.eftposClientData);
		},
		[userApi],
	);

	if (!eftposBookings || !eftposDepositAccounts || !eftposPosLists || !eftposClientData) {
		return <LoadingPanel />;
	}

	const today = new Date();
	const bookingsToday = eftposBookings.filter((booking) => {
		const bookingDate = new Date(booking.MarketDate * 1000);
		return (
			bookingDate.getDate() === today.getDate() &&
			bookingDate.getMonth() === today.getMonth() &&
			bookingDate.getFullYear() === today.getFullYear()
		);
	});

	const tomorrowAtMidnight = new Date();
	tomorrowAtMidnight.setDate(tomorrowAtMidnight.getDate() + 1);
	tomorrowAtMidnight.setHours(0, 0, 0, 0);
	const upcomingBookings = eftposBookings.filter(
		(booking) => booking.MarketDate * 1000 > tomorrowAtMidnight.getTime(),
	);

	return (
		<>
			<GstRegisteredWarning
				eftposClientData={eftposClientData}
				setGst={async (val: number) => {
					await saveEftposClientData({ ...eftposClientData, MarketClientGSTRegistered: val });
				}}
			/>
			<AbnWarning
				eftposClientData={eftposClientData}
				setAbn={() => {
					setModals((current) => ({ ...current, abnModal: true }));
				}}
				declareNoABN={async () => {
					await saveEftposClientData({ ...eftposClientData, MarketClientNoABNDeclaration: 1 });
				}}
			/>
			<MenuWarning posLists={eftposPosLists} context="dashboard" />
			<DepositAccountWarning depositAccounts={eftposDepositAccounts} context="dashboard" />
			{bookingsToday.length !== 0 && (
				<>
					<h4>Today</h4>
					<EftposBookingsTable
						bookings={bookingsToday}
						setBookings={setEftposBookings}
						posLists={eftposPosLists}
						depositAccounts={eftposDepositAccounts}
						context="dashboard"
					/>
				</>
			)}
			{upcomingBookings.length !== 0 && (
				<>
					<h4>Upcoming bookings</h4>
					<EftposBookingsTable
						bookings={upcomingBookings}
						setBookings={setEftposBookings}
						posLists={eftposPosLists}
						depositAccounts={eftposDepositAccounts}
						context="dashboard"
					/>
				</>
			)}
			<ViewBookingsLink to="/bookings">View all bookings</ViewBookingsLink>
			{eftposPosLists.length !== 0 && (
				<ManageBtn to="/menus" className="btn btn-quiet btn-block">
					Manage menus
				</ManageBtn>
			)}
			{eftposDepositAccounts.length !== 0 && (
				<ManageBtn to="/deposit-accounts" className="btn btn-quiet btn-block">
					Manage deposit accounts
				</ManageBtn>
			)}
			<Modal open={modals.abnModal} onClose={() => setModals({ ...modals, abnModal: false })}>
				<EditValueModal
					padding="3rem"
					current={''}
					label="ABN"
					buttonLabel="Save"
					onClickSet={async (newAbn: string) => {
						// save abn
						await saveEftposClientData({ ...eftposClientData, MarketClientABN: newAbn });
						setModals({ ...modals, abnModal: false });
					}}
				/>
			</Modal>
		</>
	);
}

const ManageBtn = styled(Link)`
	margin-top: 1rem;
`;

const ViewBookingsLink = styled(Link)`
	align-self: center;
`;

export const AbnWarning = (props: {
	eftposClientData: EftposClientData;
	setAbn: () => void;
	declareNoABN: () => Promise<void>;
}) => {
	const { eftposClientData, setAbn, declareNoABN } = props;
	const [loading, setLoading] = useState(false);

	if (eftposClientData.MarketClientNoABNDeclaration === 1 || eftposClientData.MarketClientABN !== '') {
		return null;
	}

	return (
		<Alert className="alert alert-warning">
			<i className="fa fa-exclamation-triangle fa-lg" />
			<span>No ABN recorded</span>
			<div className="action-group">
				<button className="ml-auto btn btn-sm btn-blue" onClick={setAbn}>
					Enter ABN
				</button>
				<button
					disabled={loading}
					className={`btn btn-sm btn-blue ${loading ? 'disabled' : ''}`}
					onClick={async () => {
						setLoading(true);
						await declareNoABN();
						setLoading(false);
					}}
				>
					I do not have an ABN
				</button>
			</div>
		</Alert>
	);
};

export const GstRegisteredWarning = (props: {
	eftposClientData: EftposClientData;
	setGst: (val: number) => Promise<void>;
}) => {
	const { eftposClientData, setGst } = props;
	const [loading, setLoading] = useState(false);

	if (eftposClientData.MarketClientGSTRegistered !== null) {
		return null;
	}

	const saveNewGst = async (val: number) => {
		setLoading(true);
		await setGst(val);
		setLoading(false);
	};

	return (
		<>
			<Alert className="alert alert-warning">
				<i className="fa fa-exclamation-triangle fa-lg"></i>
				<span>Are you registered for GST? (Being registered for GST is not required)</span>
				<div className="action-group">
					<button
						disabled={loading}
						className={`btn btn-sm btn-blue ${loading ? 'disabled' : ''}`}
						onClick={() => saveNewGst(1)}
					>
						Registered
					</button>
					<button
						disabled={loading}
						className={`btn btn-sm btn-blue ${loading ? 'disabled' : ''}`}
						onClick={() => saveNewGst(0)}
					>
						Not registered
					</button>
				</div>
			</Alert>
		</>
	);
};
